/**
 * CANONICAL RECOVERY RESOLVER — single source of truth for the customer-facing
 * "Total Supported Recovery" figure.
 *
 * Rule (v1.0, deterministic-first — supersedes the previous findings-first rule):
 *   1. Recovery ALWAYS originates from the deterministic charge-level pipeline
 *      (`computeDeterministicRecovery`): canonical invoice → charge inventory →
 *      benchmark status → recovery eligibility → Σ eligible charge recoveries.
 *   2. AI output is NEVER a source of recovery dollars. `findingsSavings` is
 *      accepted only so callers can pass what is stored, and is used purely as
 *      a diagnostic (`aiSavingsIgnored`) — it can never change the amount.
 *   3. When no charge inventory exists (no invoice line items), documented
 *      `market_research` differences are the last-resort floor, because the
 *      report publishes no charge-level scope in that case either.
 *   4. The result is always clamped to [0, invoiceTotal].
 *
 * Every surface (report, dashboard, PDF, notifications, pipeline persistence)
 * must resolve recovery through this module so no two screens can disagree.
 */

import {
  resolveChargeBenchmarkStatuses,
  type ChargeBenchmarkInputs,
} from "@/lib/report/charge-benchmark-status";
import {
  computeDeterministicRecovery,
  RECOVERY_CALCULATION_VERSION,
  type DeterministicRecoveryResult,
} from "@/lib/report/deterministic-recovery";

export interface MarketRecoveryRow {
  status?: string | null;
  difference_amount?: number | null;
}

const COMPLETED = new Set(["completed", "complete", "success", "succeeded"]);

/**
 * Sum of documented, completed above-market differences (dollars).
 * NARROWED: only a last-resort floor when no charge inventory exists — the
 * deterministic charge-level scope always takes precedence.
 */
export function documentedMarketRecovery(rows: readonly MarketRecoveryRow[] | null | undefined): number {
  if (!rows?.length) return 0;
  let sum = 0;
  for (const r of rows) {
    const status = (r.status ?? "").toLowerCase();
    if (status && !COMPLETED.has(status)) continue;
    const raw = r.difference_amount;
    const n = typeof raw === "number" ? raw : Number(raw);
    if (Number.isFinite(n) && n > 0) sum += n;
  }
  return Math.round(sum * 100) / 100;
}

/**
 * CERTIFIED SUPPORTED RECOVERY for a claim, derived from the same canonical
 * charge resolution the report renders. Returns `null` when the claim has no
 * charge inventory (nothing certified to report).
 */
export function documentedChargeRecovery(inputs: ChargeBenchmarkInputs): number | null {
  const lineItems = inputs.lineItems ?? [];
  if (!lineItems.length) return null;
  const resolution = resolveChargeBenchmarkStatuses(inputs);
  if (resolution.counts.supported === 0) return null;
  return Math.max(0, resolution.supportedRecoveryTotal);
}

/**
 * `deterministic` — computed by the charge-level engine (the only legitimate
 * origin of recovery dollars). `market_evidence` — last-resort documented
 * `market_research` floor when there is no charge inventory at all.
 * `findings` is retained in the union only so persisted historical rows can be
 * labelled; it is never produced by a new calculation.
 */
export type RecoveryBasis = "deterministic" | "market_evidence" | "none" | "findings";

export interface CanonicalRecovery {
  /** Canonical recovery in dollars, clamped to the invoice total. */
  amount: number;
  /** Where the figure came from — for provenance / audit output. */
  basis: RecoveryBasis;
  /** Rule-set identity that produced the figure. */
  calculationVersion: string;
  /** Deterministic Estimated Fair Payment and its documented band. */
  fairPayment: number | null;
  fairPaymentLow: number | null;
  fairPaymentHigh: number | null;
  /** Full charge-level lineage, when a charge inventory was available. */
  detail: DeterministicRecoveryResult | null;
  /**
   * AI-authored savings that were supplied and deliberately discarded.
   * Present only so audits can prove the AI figure had no effect.
   */
  aiSavingsIgnored: number | null;
}

export function resolveCanonicalRecovery(input: {
  /**
   * Persisted `ai_findings.estimated_savings`. ACCEPTED BUT NEVER USED as an
   * amount — kept for call-site compatibility and audit reporting only.
   */
  findingsSavings?: number | null | undefined;
  /** Charge-level inputs — the certified, report-published recovery scope. */
  chargeInputs?: ChargeBenchmarkInputs | null;
  /** Documented above-market evidence for the same review. */
  marketRows?: readonly MarketRecoveryRow[] | null;
  /** Pre-computed documented total, when the rows aren't available. */
  documentedRecovery?: number | null;
  invoiceTotal?: number | null;
}): CanonicalRecovery {
  const total =
    typeof input.invoiceTotal === "number" && Number.isFinite(input.invoiceTotal) && input.invoiceTotal > 0
      ? input.invoiceTotal
      : null;
  const clamp = (v: number) => {
    const rounded = Math.max(0, Math.round(v));
    return total !== null ? Math.min(rounded, Math.round(total)) : rounded;
  };

  const rawAi = input.findingsSavings;
  const aiSavingsIgnored = typeof rawAi === "number" && Number.isFinite(rawAi) ? rawAi : null;

  // 1 — DETERMINISTIC CHARGE-LEVEL PIPELINE. Always evaluated first.
  const hasInventory = (input.chargeInputs?.lineItems ?? []).length > 0;
  if (input.chargeInputs && hasInventory) {
    const detail = computeDeterministicRecovery({ ...input.chargeInputs, invoiceTotal: total });
    return {
      amount: clamp(detail.supportedRecovery),
      basis: "deterministic",
      calculationVersion: detail.calculationVersion,
      fairPayment: detail.fairPayment,
      fairPaymentLow: detail.fairPaymentLow,
      fairPaymentHigh: detail.fairPaymentHigh,
      detail,
      aiSavingsIgnored,
    };
  }

  // 2 — No charge inventory: documented market evidence is the only floor.
  const documented =
    typeof input.documentedRecovery === "number" && Number.isFinite(input.documentedRecovery)
      ? Math.max(0, input.documentedRecovery)
      : documentedMarketRecovery(input.marketRows ?? []);

  const amount = documented > 0 ? clamp(documented) : 0;
  return {
    amount,
    basis: amount > 0 ? "market_evidence" : "none",
    calculationVersion: RECOVERY_CALCULATION_VERSION,
    fairPayment: total !== null ? clamp(total - amount) : null,
    fairPaymentLow: null,
    fairPaymentHigh: null,
    detail: null,
    aiSavingsIgnored,
  };
}

